import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/index';
import { map, tap } from 'rxjs/operators';
import { ConnectionService } from '@services/connection-service';
import { resetCacheByName } from '@services/connection-service/cache-interceptor';
import { BroadcastService } from '@services/broadcast-service';
import { AddressBookI } from '@store/modals';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { EventLoggerService } from '@services/event-logger-service';
import { Experiment } from '@cureskin/api-client/src/server-api';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { UserAddressBottomsheetComponent } from '../user-address-bottomsheet/user-address-bottomsheet.component';
import { AppConfig } from 'client/app/app.config';
import { ShiprocketService } from '@services/shiprocket-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { CheckoutService } from '@services/checkout-service';
import { DELIVERY_DATE_VARIANT } from '@shared/expected-delivery-date/expected-delivery-date.component';

const QUERY_PARAMS_TO_PRESERVE = ['type', 'regimenId', 'useCureSkinCash', 'products', 'messageId', 'services', 'ref', 'source', 'shiprocketPhone', 'fromReviewOrder'];
const OTP_LENGTH = 6;
const OTP_RESEND_SECONDS = 30;
const ERROR_MESSAGES = {
  PHONE_NOT_FOUND: 'Phone number not found. Please try again.',
  IMPORT_FAILED: 'Unable to import address. Please try again.',
  EDIT_FAILED: 'Unable to open address for edit. Please try again.',
  FETCH_FAILED: 'Unable to fetch Shiprocket addresses. Please try again.',
};


@Component({
  selector: 'user-address-list',
  templateUrl: './user-address-list.html',
  standalone: false,
})

export class UserAddressListComponent implements OnInit {
  readonly DELIVERY_DATE_VARIANT = DELIVERY_DATE_VARIANT;
  addressBookList$: Observable<AddressBookI[]>;
  isLoading$: Observable<boolean>;
  checkoutInProgress$: Observable<boolean>;
  defaultAddress$: Observable<AddressBookI>;
  otherAddressList$: Observable<AddressBookI[]>;
  shiprocketAddresses: AddressBookI[] = [];
  isShiprocketFlow: boolean = false;
  shiprocketHowExpanded: boolean = false;
  shiprocketPhone: string = '';
  private _deliverTo: AddressBookI;
  deliveryDate: { from?: Date; to?: Date } = {};
  deliveryDayDiff: number | undefined;
  private importedShiprocketAddresses: Set<string> = new Set(); // Track imported addresses to prevent duplicates
  loading: boolean = true;
  inProcess: boolean = false;
  discount: number = 0;
  productsInCart: { [key: string]: number } = {};
  products: any;
  internalUser: boolean = false;
  experiments: Experiment[] = [];
  hasDefaultAddress: boolean = false;
  hasOtherAddresses: boolean = false;
  isFromReviewOrder: boolean = false;

  constructor(
    private conn: ConnectionService,
    private appWebBridgeService: AppWebBridgeService,
    private broadcast: BroadcastService,
    private route: ActivatedRoute,
    private eventLogger: EventLoggerService,
    private store: Store,
    private router: Router,
    private bottomSheet: MatBottomSheet,
    private shiprocketService: ShiprocketService,
    private readonly checkoutService: CheckoutService,
  ) {
  }

  get deliverTo(): AddressBookI { return this._deliverTo; }
  set deliverTo(address: AddressBookI) {
    this._deliverTo = address;
    if (this.isFromReviewOrder) {
      this.deliveryDate = {};
      this.deliveryDayDiff = undefined;
      if (address?.zipCode) {
        void this.fetchDeliveryDate(address.zipCode.toString());
      }
    }
  }

  get hasAnyAddress(): boolean {
    return this.hasDefaultAddress || this.hasOtherAddresses;
  }

  get shouldShowRebrandedContinue(): boolean {
    if (!this.hasAnyAddress) return false;
    return this.isShiprocketFlow;
  }

  get shouldShowExistingContinue(): boolean {
    if (!this.hasAnyAddress) return false;
    return !this.isShiprocketFlow;
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridgeService.setStatusBarColor('black');
    this.internalUser = this.conn.isInternalUser();
    const params = this.route.snapshot.queryParams;
    await this.setUserExperiments();
    this.isFromReviewOrder = this.route.snapshot.queryParamMap.has('fromReviewOrder');
    this.initShiprocketFlow(params);
    this.initProductsAndRegimen(params);
    this.initObservables();
    if (this.isShiprocketFlow && this.shiprocketPhone) {
      await this.fetchShiprocketAddresses();
    } else {
      await this.store.dispatch(fromActions.AddressBookListFetchBegin({}));
    }
    this.loading = false;
  }

  private initShiprocketFlow(params: any): void {
    this.isShiprocketFlow = params.source === AppConfig.Shared.Shiprocket.Source && this.shiprocketService.isAcquisitionUser();
    this.shiprocketPhone = params[AppConfig.Shared.Shiprocket.PhoneParam] || '';
  }

  private initProductsAndRegimen(params: any): void {
    if (params.products) {
      this.products = params.products.split(',');
    }
  }

  private initObservables(): void {
    this.isLoading$ = this.store.select(fromSelectors.selectIsFetchingAddress);
    this.checkoutInProgress$ = this.store.select(fromSelectors.selectCheckoutProgress);
    this.addressBookList$ = this.store.select(fromSelectors.selectAddressBookList);
    this.defaultAddress$ = this.addressBookList$
      .pipe(
        map((data: AddressBookI[]): AddressBookI => data.find((each: AddressBookI): boolean => each.default)),
        tap((defaultAddress: AddressBookI): AddressBookI => {
          this.deliverTo = defaultAddress;
          this.hasDefaultAddress = !!defaultAddress;
          return defaultAddress;
        }));
    this.otherAddressList$ = this.addressBookList$
      .pipe(
        map((data: AddressBookI[]): AddressBookI[] => data.filter((each: AddressBookI): boolean => !each.default)),
        tap((list: AddressBookI[]): AddressBookI[] => {
          this.hasOtherAddresses = !!list?.length;
          return list;
        }));
  }

  async setUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
  }

  isSelectedAddress(address: AddressBookI): boolean {
    return this.deliverTo?.objectId === address?.objectId;
  }

  shouldShowCardHighlight(address: AddressBookI): boolean {
    return this.isShiprocketFlow || !this.isFromReviewOrder || this.isSelectedAddress(address);
  }

  shouldShowDeliveryDate(address: AddressBookI): boolean {
    return this.isFromReviewOrder && this.isSelectedAddress(address);
  }

  toggleShiprocketHow(): void {
    this.shiprocketHowExpanded = !this.shiprocketHowExpanded;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'how-it-works',
      state: this.shiprocketHowExpanded ? 'open' : 'closed',
    }));
  }

  get shouldShowAddressSectionHeaders(): boolean {
    return !this.isFromReviewOrder;
  }

  private async fetchDeliveryDate(pincode: string): Promise<void> {
    const lang = this.conn.getActingUser()?.get('languagePreference') ?? '';
    const result = await this.checkoutService.fetchDeliveryDateInfo(pincode, lang);
    if (this._deliverTo?.zipCode?.toString() === pincode) {
      this.deliveryDate = result.expectedDeliveryDate;
      this.deliveryDayDiff = result.dayDifference;
    }
  }

  navigateTo(url: string): void {
    this.conn.navigateToURL(url);
  }

  editAddress(address: any, index?: number): void {
    if (this.isShiprocketFlow) {
      this.trackShiprocketClickEvent('shiprocket_address_edit');
      if (address?.objectId) {
        const url = this.buildEditUrl(address.objectId);
        this.conn.navigateToURL(url);
        return;
      }
      if (typeof index === 'number') {
        void this.editShiprocketAddress(index);
        return;
      }
    }
    const editUrl = this.isFromReviewOrder
      ? `/user/address/${address.objectId}/edit?fromReviewOrder=true&fromAddressList=true`
      : `/user/address/${address.objectId}/edit`;
    this.navigateTo(editUrl);
  }

  private normalizeAddressForComparison(address: any): string {
    const plainAddr = address?.toJSON ? address.toJSON() : address;
    const getField = (field: string): unknown => {
      if (plainAddr[field] !== undefined) return plainAddr[field];
      if (address?.get && typeof address.get === 'function') {
        try {
          return address.get(field);
        } catch {
          return undefined;
        }
      }
      return undefined;
    };

    const safe = (v: unknown): string => {
      if (v === null || v === undefined) return '';
      if (typeof v === 'string') return v.trim().toLowerCase();
      return `${v}`.trim().toLowerCase();
    };

    return JSON.stringify({
      buildingDetails: safe(getField('buildingDetails')),
      localityDetails: safe(getField('localityDetails')),
      landmark: safe(getField('landmark')),
      city: safe(getField('city')),
      state: safe(getField('state')),
      zipCode: safe(getField('zipCode')),
      mobileNumber: safe(getField('mobileNumber')),
    });
  }

  private async findExistingDuplicateAddress(candidate: AddressBookI): Promise<AddressBookI | null> {
    const user = this.conn.getActingUser();
    if (!user) return null;

    try {
      const existingAddresses = await this.conn.fetchAllAddress(user);
      const candidateKey = this.normalizeAddressForComparison(candidate);
      const plainAddresses = this.convertToPlainAddresses(existingAddresses);

      const match = await this.findMatchInPlainAddresses(plainAddresses, candidateKey);
      if (match) return match;

      return this.findMatchInDecryptedAddresses(plainAddresses, candidateKey);
    } catch {
      return null;
    }
  }

  private convertToPlainAddresses(addresses: any[]): any[] {
    return addresses.map((addr: any): any => {
      if (addr?.toJSON && typeof addr.toJSON === 'function') {
        return addr.toJSON();
      }
      return JSON.parse(JSON.stringify(addr));
    });
  }

  private async findMatchInPlainAddresses(plainAddresses: any[], candidateKey: string): Promise<AddressBookI | null> {
    for (const plainAddr of plainAddresses) {
      const plainKey = this.normalizeAddressForComparison(plainAddr);
      if (plainKey === candidateKey) {
        const mobileNumber = plainAddr.mobileNumber || plainAddr.get?.('mobileNumber');
        if (plainAddr.objectId && (mobileNumber?.length > 15 || /[^0-9]/.test(mobileNumber))) {
          try {
            const decryptedAddr = await this.conn.fetchAddressById(plainAddr.objectId);
            return JSON.parse(JSON.stringify(decryptedAddr)) as AddressBookI;
          } catch {
            return plainAddr as AddressBookI;
          }
        }
        return plainAddr as AddressBookI;
      }
    }
    return null;
  }

  private async findMatchInDecryptedAddresses(plainAddresses: any[], candidateKey: string): Promise<AddressBookI | null> {
    for (const plainAddr of plainAddresses) {
      if (plainAddr?.objectId) {
        try {
          const decryptedAddr = await this.conn.fetchAddressById(plainAddr.objectId);
          const decryptedKey = this.normalizeAddressForComparison(decryptedAddr);
          if (decryptedKey === candidateKey) {
            return JSON.parse(JSON.stringify(decryptedAddr)) as AddressBookI;
          }
        } catch {
          continue;
        }
      }
    }
    return null;
  }

  private async importShiprocketAddressByIndex(index: number, phone: string): Promise<AddressBookI | null> {
    const candidate = this.shiprocketAddresses[index];
    if (!candidate) return null;

    const addressKey = this.normalizeAddressForComparison(candidate);

    if (this.importedShiprocketAddresses.has(addressKey)) {
      const existing = await this.findExistingDuplicateAddress(candidate);
      if (existing?.objectId) {
        return existing;
      }
      this.importedShiprocketAddresses.delete(addressKey);
    }

    const existing = await this.findExistingDuplicateAddress(candidate);
    if (existing?.objectId) {
      this.importedShiprocketAddresses.add(addressKey);
      return existing;
    }

    return this.performShiprocketImport(index, phone, addressKey);
  }

  private async performShiprocketImport(index: number, phone: string, addressKey: string): Promise<AddressBookI | null> {
    try {
      const response = await this.conn.importShiprocketAddress({ addressIndex: index, phone });
      const importedAddress = response?.address as AddressBookI | undefined;

      if (!importedAddress?.objectId) return null;

      const decryptedAddress = await this.conn.fetchAddressById(importedAddress.objectId);
      const decryptedAddressJson = JSON.parse(JSON.stringify(decryptedAddress)) as AddressBookI;

      this.importedShiprocketAddresses.add(addressKey);
      return decryptedAddressJson;
    } catch {
      return null;
    }
  }

  getDeliveryAddress(defaultAddress: AddressBookI): string {
    const address = [];
    address.push(defaultAddress.buildingDetails);
    address.push(defaultAddress.localityDetails);
    address.push(defaultAddress.city);
    address.push(defaultAddress.state);
    address.push(defaultAddress.zipCode);
    return address.filter((each: string): boolean => !!each).join(', ');
  }

  async deleteAddress(address: AddressBookI): Promise<void> {
    this.store.dispatch(fromActions.AddressBookDeleteBegin({ address }));
  }

  async continueFromShiprocketFlow(): Promise<void> {
    if (!this.deliverTo) return;
    this.inProcess = true;
    this.store.dispatch(fromActions.AddressBookUpdate({
      payload: { defaultAddress: this.deliverTo },
    }));
    resetCacheByName('fetchDefaultAddress');
    await this.routeAfterAddressSelected();
    this.inProcess = false;
  }

  async saveAsDeliveryAddress(): Promise<void> {
    if (!this.deliverTo) return;
    this.inProcess = true;
    this.store.dispatch(fromActions.AddressBookSetDeliveryAddress({
      deliveryAddress: this.deliverTo,
      skipNavigationBack: this.isFromReviewOrder,
    }));
    this.eventLogger.cleverTapEvent('click-selected-old-address', JSON.stringify({ addressId: this.deliverTo?.objectId }));
    if (this.isFromReviewOrder) {
      await this.checkoutService.initiateRegimenCheckout(this.route.snapshot.queryParams, this.conn.getActingUser());
      this.inProcess = false;
    }
  }

  async selectShiprocketAddress(eventName: string = 'shiprocket_address_selected'): Promise<void> {
    if (!this.canSelectShiprocketAddress()) return;

    const index = this.findShiprocketAddressIndex();
    if (index < 0) return;

    this.trackShiprocketClickEvent(eventName);

    try {
      this.inProcess = true;
      const phone = this.getShiprocketPhone();
      if (!phone) {
        this.handleError(ERROR_MESSAGES.PHONE_NOT_FOUND);
        return;
      }

      const importedOrExisting = await this.importShiprocketAddressByIndex(index, phone);
      if (!importedOrExisting?.objectId) {
        this.handleError(ERROR_MESSAGES.IMPORT_FAILED);
        return;
      }

      this.updateDefaultAddress(importedOrExisting);
      resetCacheByName('fetchDefaultAddress');
      await this.routeAfterAddressSelected();
      this.inProcess = false;
    } catch {
      this.handleError(ERROR_MESSAGES.IMPORT_FAILED);
    }
  }

  private async routeAfterAddressSelected(): Promise<void> {
    if (this.isFromReviewOrder) {
      await this.checkoutService.initiateRegimenCheckout(this.route.snapshot.queryParams, this.conn.getActingUser());
      return;
    }
    await this.navigateToCheckout();
  }

  private canSelectShiprocketAddress(): boolean {
    return this.shiprocketService.isAcquisitionUser() && this.isShiprocketFlow && !!this.deliverTo && !!this.shiprocketAddresses?.length;
  }

  private findShiprocketAddressIndex(): number {
    return this.shiprocketAddresses.findIndex(
      (addr: AddressBookI): boolean => addr.objectId === this.deliverTo.objectId,
    );
  }

  private getShiprocketPhone(): string {
    const params = this.route.snapshot.queryParams || {};
    return params.shiprocketPhone || this.shiprocketPhone;
  }

  private updateDefaultAddress(address: AddressBookI): void {
    this.store.dispatch(fromActions.AddressBookUpdate({
      payload: {
        defaultAddress: address,
      },
    }));
  }

  private async navigateToCheckout(): Promise<void> {
    const params = this.route.snapshot.queryParams || {};
    const checkoutUrl = this.buildCheckoutUrl(params);
    await this.conn.navigateToURL(checkoutUrl);
  }

  private buildCheckoutUrl(params: Record<string, any>): string {
    const keys = Object.keys(params);
    if (!keys.length) return '/user/checkout';
    const qs = keys
      .map((key: string): string => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
      .join('&');
    return `/user/checkout?${qs}`;
  }

  private handleError(message: string): void {
    this.inProcess = false;
    this.broadcast.broadcast('NOTIFY', { message });
  }

  private async editShiprocketAddress(index: number): Promise<void> {
    if (!this.isShiprocketFlow || !this.shiprocketAddresses?.length) return;

    try {
      this.inProcess = true;
      const phone = this.getShiprocketPhone();
      if (!phone) {
        this.handleError(ERROR_MESSAGES.PHONE_NOT_FOUND);
        return;
      }

      const importedOrExisting = await this.importShiprocketAddressByIndex(index, phone);
      if (!importedOrExisting?.objectId) {
        this.handleError(ERROR_MESSAGES.IMPORT_FAILED);
        return;
      }

      const url = this.buildEditUrl(importedOrExisting.objectId);
      await this.conn.navigateToURL(url);
      this.inProcess = false;
    } catch {
      this.handleError(ERROR_MESSAGES.EDIT_FAILED);
    }
  }

  private buildEditUrl(addressId: string): string {
    const params = this.route.snapshot.queryParams || {};
    const keys = Object.keys(params);
    let url = `/user/address/${addressId}/edit`;
    if (keys.length) {
      const qs = this.buildQueryString(params);
      url += `?${qs}`;
    }
    return url;
  }

  private buildQueryString(params: Record<string, any>): string {
    return Object.keys(params)
      .map((key: string): string => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
      .join('&');
  }

  addNewAddressClick(): void {
    this.eventLogger.cleverTapEvent('click-add-new-address', JSON.stringify({ addressId: this.deliverTo?.objectId }));
    this.trackShiprocketClickEvent('shiprocket_add_new_address');
    const params = this.route.snapshot.queryParams || {};
    const preservedParams = this.filterPreservedParams(params);
    if (this.isFromReviewOrder) {
      preservedParams.fromAddressList = 'true';
    }
    const url = this.buildNewAddressUrl(preservedParams);
    this.navigateTo(url);
  }

  private filterPreservedParams(params: Record<string, any>): Record<string, any> {
    const preserved: Record<string, any> = {};
    QUERY_PARAMS_TO_PRESERVE.forEach((key: string): void => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        preserved[key] = params[key];
      }
    });
    return preserved;
  }

  private buildNewAddressUrl(params: Record<string, any>): string {
    const keys = Object.keys(params);
    if (!keys.length) return '/user/address/new';
    const qs = this.buildQueryString(params);
    return `/user/address/new?${qs}`;
  }

  async fetchShiprocketAddresses(): Promise<void> {
    const params = this.route.snapshot.queryParams || {};
    const phone = params.shiprocketPhone;
    if (!phone) return;

    let count = 0;
    try {
      const response = await this.conn.importAllShiprocketAddresses({ phone });
      count = response?.addresses?.length ?? 0;
      this.shiprocketService.storeAddressData(phone, count);
      this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({
        pageName: 'shiprocket_address_found',
        no_of_address: count,
      }));
    } catch {
      count = 0;
    }

    await this.store.dispatch(fromActions.AddressBookListFetchBegin({}));
  }

  private assignUniqueIds(addresses: AddressBookI[]): AddressBookI[] {
    return addresses.map((addr: AddressBookI, index: number): AddressBookI => ({
      ...addr,
      objectId: addr.objectId || `shiprocket-${index}`,
    }));
  }

  private updateStoreWithShiprocketAddresses(): void {
    this.store.dispatch(fromActions.AddressBookUpdate({
      payload: {
        addressList: JSON.parse(JSON.stringify(this.shiprocketAddresses)),
        isFetchInProgress: false,
      },
    }));
  }

  private setDefaultShiprocketAddress(): void {
    if (this.shiprocketAddresses.length) {
      this.deliverTo = this.shiprocketAddresses[0];
    }
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  viewDetails(): void {
    if (this.products) {
      const params = this.route.snapshot.queryParams;
      const queryParams = { products: params.products, showAll: true };
      this.router.navigate(['user/order/reorder'], { queryParams });
    }
  }

  async tryWithAnotherNumber(): Promise<void> {
    if (!this.shiprocketService.isAcquisitionUser()) return;
    const user = this.conn.getActingUser();
    if (!user) return;

    this.trackShiprocketClickEvent('shiprocket_try_other_number');
    this.bottomSheet.open(UserAddressBottomsheetComponent, {
      data: {
        mode: AppConfig.Shared.AddressBook.BottomsheetMode.Mobile,
        mobileNumber: '',
        otpLength: OTP_LENGTH,
        resendSeconds: OTP_RESEND_SECONDS,
        onSendOtp: (mobileNumber: string): Promise<boolean> => this.handleShiprocketOtpRequest(mobileNumber),
        onVerifyOtp: (otp: string, mobileNumber: string): Promise<boolean> => {
          return this.verifyShiprocketOTP(otp, mobileNumber);
        },
        onResendOtp: (mobileNumber: string): Promise<boolean> => this.handleShiprocketOtpRequest(mobileNumber),
      },
    });
  }

  addAddressManually(): void {
    this.trackShiprocketClickEvent('shiprocket_enter_manually');
    const params = this.route.snapshot.queryParams || {};
    const { shiprocketPhone, ...restParams } = params;
    const url = this.buildNewAddressUrl(restParams);
    this.conn.navigateToURL(url);
  }

  private async handleShiprocketOtpRequest(mobileNumber?: string): Promise<boolean> {
    return this.shiprocketService.handleOtpRequest(mobileNumber);
  }

  private async verifyShiprocketOTP(otp: string, mobileNumber: string): Promise<boolean> {
    return this.shiprocketService.verifyOTP(otp, mobileNumber, async (verifiedMobileNumber: string): Promise<void> => {
      const currentParams = this.route.snapshot.queryParams || {};
      const mergedParams: Record<string, any> = {
        ...currentParams,
        source: AppConfig.Shared.Shiprocket.Source,
        shiprocketPhone: verifiedMobileNumber,
      };
      const qs = this.buildQueryString(mergedParams);
      this.conn.navigateToURL(`/user/address?${qs}`);
    });
  }

  trackShiprocketClickEvent(eventName: string): void {
    if (!this.isShiprocketFlow) return;
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: eventName }));
  }
}
